//
// Copyright 2025 DXOS.org
//

import { type Completion } from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { debounce } from '@dxos/async';
import { type Type } from '@dxos/echo';
import { Format, TypeEnum } from '@dxos/echo/Format';
import { invariant } from '@dxos/invariant';
import { type DxGridAxis, type DxGridPosition } from '@dxos/lit-grid';
import {
  type EditorBlurHandler,
  type EditorKeyEvent,
  type EditorKeyOrBlurHandler,
  GridCellEditor,
  type GridCellEditorProps,
  type GridEditing,
  editorKeys,
  parseCellIndex,
  useGridContext,
} from '@dxos/react-ui-grid';
import { type FieldProjection } from '@dxos/schema';

import { type ModalController, type TableModel } from '../../model/index.ts';
import { CellValidationMessage } from './CellValidationMessage.tsx';
import { FormCellEditor, type OnCreateHandler } from './FormCellEditor.tsx';

/**
 * Option to create new object/value.
 */
export type QueryResult = Pick<Completion, 'label'> & {
  data: any;
};

export type TableCellEditorProps<T extends Type.AnyEntity = Type.AnyEntity> = {
  schema?: T;
  model?: TableModel;
  modals?: ModalController;
  onFocus?: (axis?: DxGridAxis, delta?: -1 | 0 | 1, cell?: DxGridPosition) => void;
  onCreate?: OnCreateHandler;
  onSave?: () => void;
};

/**
 * Resolves the field projection for the cell being edited, guarding against an out-of-range field
 * index (e.g. a stale cell, or a pinned plane whose `getFieldIndex` maps beyond the projection).
 */
const resolveFieldProjection = (
  model: TableModel | undefined,
  editing: GridEditing | undefined,
): FieldProjection | undefined => {
  if (!model || !editing) {
    return undefined;
  }

  const { col, plane } = parseCellIndex(editing.index);
  const fieldIndex = model.getFieldIndex(plane, col);
  const fields = model.projection.getFields();
  if (fieldIndex === undefined || fieldIndex < 0 || fieldIndex >= fields.length) {
    return undefined;
  }

  const fieldProjection = model.projection.getFieldProjection(fields[fieldIndex].id);
  invariant(fieldProjection);
  return fieldProjection;
};

export const TableValueEditor = <T extends Type.AnyEntity = Type.AnyEntity>({
  schema,
  model,
  modals,
  onFocus,
  onSave,
  onCreate,
}: TableCellEditorProps<T>) => {
  const { editing } = useGridContext('TableValueEditor');

  const fieldProjection = useMemo(() => resolveFieldProjection(model, editing), [model, editing]);

  if (
    fieldProjection?.props.type === TypeEnum.Array ||
    fieldProjection?.props.format === Format.TypeFormat.SingleSelect ||
    fieldProjection?.props.format === Format.TypeFormat.Ref
    // TODO(thure): Support `Format.TypeFormat.MultiSelect`
  ) {
    return (
      <FormCellEditor<T>
        schema={schema}
        model={model}
        fieldProjection={fieldProjection}
        modals={modals}
        onSave={onSave}
        onCreate={onCreate}
      />
    );
  }

  // For all other types, use the existing cell editor.
  return <TableCellEditor model={model} modals={modals} onFocus={onFocus} onSave={onSave} />;
};

const cellEditorSlots: GridCellEditorProps['slots'] = {
  content: {
    className: '!py-(--dx-grid-cell-editor-padding-block)',
  },
};

export const TableCellEditor = ({ model, modals, onFocus, onSave }: TableCellEditorProps) => {
  const { editing, setEditing } = useGridContext('TableCellEditor');
  const suppressNextBlur = useRef(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationVariant, setValidationVariant] = useState<'error' | 'warning'>('error');

  const fieldProjection = useMemo(() => resolveFieldProjection(model, editing), [model, editing]);

  // TOOD(burdon): Attach to event handler?
  const handleEnter = useCallback(
    async (value: any) => {
      if (!model || !editing) {
        return;
      }

      const cell = parseCellIndex(editing.index);
      const validationResult = await model.validateCellData(cell, value);

      if (validationResult.valid) {
        setValidationError(null);
        model.setCellData(cell, value);
        onFocus?.();
        setEditing(null);
        onSave?.();
      } else {
        setValidationError(validationResult.error);
        setValidationVariant('error');
      }
    },
    [model, editing, onFocus, setEditing],
  );

  const handleBlur = useCallback<EditorBlurHandler>(
    async (value) => {
      if (suppressNextBlur.current) {
        suppressNextBlur.current = false;
      } else if (model && editing) {
        // Save silently if validation passes
        const cell = parseCellIndex(editing.index);
        if (value !== undefined) {
          const result = await model.validateCellData(cell, value);
          if (result.valid) {
            setValidationError(null);
            model.setCellData(cell, value);
            setEditing(null);
            onSave?.();
          }
        }
      }
    },
    [model, editing],
  );

  const handleClose = useCallback<EditorKeyOrBlurHandler>(
    async (value, event) => {
      if (!model || !editing || !fieldProjection) {
        return;
      }

      const cell = parseCellIndex(editing.index);
      if (value !== undefined) {
        // Pre-commit validation check.
        const result = await model.validateCellData(cell, value);

        if (result.valid) {
          suppressNextBlur.current = true;
          setValidationError(null);
          model.setCellData(cell, value);
          setEditing(null);
          onSave?.();
          if (event && onFocus) {
            onFocus(determineNavigationAxis(event), determineNavigationDelta(event), cell);
          }
        } else {
          setValidationError(result.error);
          setValidationVariant('error');
        }
      } else {
        suppressNextBlur.current = true;
        setValidationError(null);
        setEditing(null);
        onSave?.();
        if (event && onFocus) {
          onFocus(determineNavigationAxis(event), determineNavigationDelta(event));
        }
      }
    },
    [model, editing, onFocus, fieldProjection, setEditing, onSave],
  );

  const extensions = useMemo(() => {
    if (!fieldProjection) {
      return [];
    }

    const extensions = [
      editorKeys({
        onClose: handleClose,
        ...(editing?.initialContent && { onNav: handleClose }),
      }),
    ];
    // Add validation extension to handle content changes.
    if (model && editing) {
      extensions.push(
        EditorView.updateListener.of(
          debounce((update) => {
            const content = update.state.doc.toString();
            const cell = parseCellIndex(editing.index);

            // Perform validation on content change.
            void model.validateCellData(cell, content).then((result) => {
              if (result.valid) {
                setValidationError(null);
              } else {
                setValidationError(result.error);
                setValidationVariant('error');
              }
            });
          }, 10),
        ),
      );
    }

    return extensions;
  }, [model, modals, editing, fieldProjection, handleClose]);

  const getCellContent = useCallback<GridCellEditorProps['getCellContent']>(() => {
    if (model && editing) {
      const cell = parseCellIndex(editing.index);
      const value = model.getCellData(cell);
      return value !== undefined ? String(value) : '';
    }
  }, [model, editing]);

  return (
    <>
      <CellValidationMessage validationError={validationError} variant={validationVariant} />
      <GridCellEditor
        extensions={extensions}
        slots={cellEditorSlots}
        getCellContent={getCellContent}
        onBlur={handleBlur}
      />
    </>
  );
};

const determineNavigationAxis = ({ key }: EditorKeyEvent): 'col' | 'row' | undefined => {
  switch (key) {
    case 'ArrowUp':
    case 'ArrowDown':
    case 'Enter': {
      return 'row';
    }

    case 'ArrowLeft':
    case 'ArrowRight':
    case 'Tab':
      return 'col';
  }
};

const determineNavigationDelta = ({ key, shift }: EditorKeyEvent): -1 | 1 => {
  switch (key) {
    case 'ArrowUp':
    case 'ArrowLeft':
      return -1;

    case 'ArrowDown':
    case 'ArrowRight':
      return 1;
  }

  return shift ? -1 : 1;
};
